import * as React from 'react';

import type {
  DragCancelEvent,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  DraggableAttributes,
} from '@dnd-kit/core';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, cn } from '@rematter/ui-web';
import { useAtom } from 'jotai';

import { InventoryItemButton } from '../InventoryItemButton';

import { EditCardOverlays } from './EditCardOverlays';
import { updateFavoriteOrdering } from './favoriteOrdering';
import { orderEntitiesByIds, updateOrderedIds } from './orderedIds';

import {
  HueColorPicker,
  hueToColor,
} from '~/modules/trade/_components/scale-ticket-pro/shared/HueColorPicker';
import {
  $touchModalColorChanges,
  $touchModalItemFavoritesByGroup,
  $touchModalItemOrderByGroup,
  type TouchModalColorChange,
} from '~/modules/trade/_state/scaleTicketSettings.state';
import { useInventoryGroupStyle } from '~/modules/trade/hooks/useInventoryGroupStyle';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { InventoryItemRecord } from '~/modules/trade/types/load';
import { usePermission } from '~/shared/hooks/usePermission';

type EditModeGroupViewProps = {
  selectedGroupId: string;
  selectedGroupName: string;
  itemsForGroup: InventoryItemRecord[];
  groupColorHue: number | null | undefined;
  searchTerm: string;
};

//
// ⚛️ ⚛️ ⚛️ EDIT ITEM CARD ⚛️ ⚛️ ⚛️
//
type EditItemCardProps = {
  item: InventoryItemRecord;
  effectiveColorHue: number | null | undefined;
  isFavorite: boolean;
  canEditColors: boolean;
  onToggleFavorite: () => void;
  onColorSwatchPress: () => void;
  sortableRef?: (node: HTMLElement | null) => void;
  sortableStyle?: React.CSSProperties;
  dragAttributes?: DraggableAttributes;
  dragListeners?: ReturnType<typeof useSortable>['listeners'];
  className?: string;
};

const EditItemCard = ({
  item,
  effectiveColorHue,
  isFavorite,
  canEditColors,
  onToggleFavorite,
  onColorSwatchPress,
  sortableRef,
  sortableStyle,
  dragAttributes,
  dragListeners,
  className,
}: EditItemCardProps) => {
  const itemStyle = useInventoryGroupStyle({
    colorHue: effectiveColorHue ?? undefined,
  });

  return (
    <div
      ref={sortableRef}
      style={sortableStyle}
      className={cn('@EditItemCard relative w-full', className)}
    >
      <InventoryItemButton
        inventoryItem={item}
        onPress={onToggleFavorite}
        leftElement={item.mnemonic}
        align="left"
        variant="subtle"
        overrideGroupStyle
        className="!w-full"
        style={itemStyle}
      />

      <EditCardOverlays
        isFavorite={isFavorite}
        canEditColors={canEditColors}
        colorHue={effectiveColorHue}
        onToggleFavorite={onToggleFavorite}
        onColorSwatchPress={onColorSwatchPress}
        dragAttributes={dragAttributes}
        dragListeners={dragListeners}
      />
    </div>
  );
};

//
// ⚛️ ⚛️ ⚛️ SORTABLE EDIT ITEM CARD ⚛️ ⚛️ ⚛️
//
const SortableEditItemCard = (
  props: Omit<
    React.ComponentProps<typeof EditItemCard>,
    'sortableRef' | 'sortableStyle' | 'dragAttributes' | 'dragListeners'
  > & { id: string },
) => {
  const { id, ...rest } = props;
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  return (
    <EditItemCard
      {...rest}
      sortableRef={setNodeRef}
      sortableStyle={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      dragAttributes={attributes}
      dragListeners={listeners}
      className="@SortableEditItemCard"
    />
  );
};

//
// ⚛️ ⚛️ ⚛️ EDIT MODE GROUP VIEW ⚛️ ⚛️ ⚛️
//
export const EditModeGroupView = ({
  selectedGroupId,
  selectedGroupName,
  itemsForGroup,
  groupColorHue,
  searchTerm,
}: EditModeGroupViewProps) => {
  const t = useTranslations();

  const [itemFavoritesByGroup, setItemFavoritesByGroup] = useAtom(
    $touchModalItemFavoritesByGroup,
  );
  const [itemOrderByGroup, setItemOrderByGroup] = useAtom(
    $touchModalItemOrderByGroup,
  );
  const [colorChanges, setColorChanges] = useAtom($touchModalColorChanges);

  const { allowed: canEditColors } = usePermission('itemUpdate');
  const { allowed: canEditGroupColors } = usePermission('itemGroupUpdate');

  const [colorPickerItemId, setColorPickerItemId] = React.useState<
    string | null
  >(null);
  const [groupColorPickerOpen, setGroupColorPickerOpen] = React.useState(false);
  const dragSnapshotRef = React.useRef<{
    favoriteIds: string[];
    orderedIds: string[];
  } | null>(null);
  const lastDragTargetRef = React.useRef<string | null>(null);
  const favoriteIdsRef = React.useRef<string[]>([]);
  const orderedIdsRef = React.useRef<string[]>([]);
  const itemFavoritesByGroupRef = React.useRef(itemFavoritesByGroup);
  const itemOrderByGroupRef = React.useRef(itemOrderByGroup);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    }),
    useSensor(KeyboardSensor),
  );

  const groupStyle = useInventoryGroupStyle({
    colorHue: groupColorHue ?? undefined,
  });

  const itemsById = React.useMemo(
    () => new Map(itemsForGroup.map((item) => [item.id, item])),
    [itemsForGroup],
  );

  const favoriteItemIds = React.useMemo(() => {
    const favs = itemFavoritesByGroup[selectedGroupId] ?? [];
    const itemSet = new Set(itemsForGroup.map((i) => i.id));
    return favs.filter((id) => itemSet.has(id));
  }, [itemFavoritesByGroup, selectedGroupId, itemsForGroup]);
  const effectiveItemOrderIds = React.useMemo(
    () =>
      itemOrderByGroup[selectedGroupId]?.length
        ? [
            ...itemOrderByGroup[selectedGroupId],
            ...itemsForGroup.map((item) => item.id),
          ].filter(
            (id, index, ids) => ids.indexOf(id) === index && itemsById.has(id),
          )
        : itemsForGroup.map((item) => item.id),
    [itemOrderByGroup, selectedGroupId, itemsForGroup, itemsById],
  );
  const orderedItemsForGroup = React.useMemo(
    () => orderEntitiesByIds(itemsForGroup, effectiveItemOrderIds),
    [itemsForGroup, effectiveItemOrderIds],
  );

  React.useEffect(() => {
    favoriteIdsRef.current = favoriteItemIds;
  }, [favoriteItemIds]);

  React.useEffect(() => {
    orderedIdsRef.current = effectiveItemOrderIds;
  }, [effectiveItemOrderIds]);

  React.useEffect(() => {
    itemFavoritesByGroupRef.current = itemFavoritesByGroup;
  }, [itemFavoritesByGroup]);

  React.useEffect(() => {
    itemOrderByGroupRef.current = itemOrderByGroup;
  }, [itemOrderByGroup]);

  const favoriteSet = React.useMemo(
    () => new Set(favoriteItemIds),
    [favoriteItemIds],
  );

  const nonFavoriteItems = React.useMemo(
    () => orderedItemsForGroup.filter((item) => !favoriteSet.has(item.id)),
    [orderedItemsForGroup, favoriteSet],
  );

  const filteredFavoriteIds = React.useMemo(() => {
    if (!searchTerm) return favoriteItemIds;
    const term = searchTerm.toLowerCase();
    return favoriteItemIds.filter((id) => {
      const item = itemsById.get(id);
      return item?.name.toLowerCase().includes(term);
    });
  }, [favoriteItemIds, searchTerm, itemsById]);

  const filteredNonFavorites = React.useMemo(() => {
    if (!searchTerm) return nonFavoriteItems;
    const term = searchTerm.toLowerCase();
    return nonFavoriteItems.filter((item) =>
      item.name.toLowerCase().includes(term),
    );
  }, [nonFavoriteItems, searchTerm]);

  const effectiveGroupColorHue = React.useMemo(() => {
    const change = colorChanges[selectedGroupId];
    if (change?.colorHue != null) return change.colorHue;
    return groupColorHue ?? null;
  }, [colorChanges, selectedGroupId, groupColorHue]);

  const getItemColorHue = React.useCallback(
    (itemId: string): number | null => {
      const change = colorChanges[itemId];
      if (change?.colorHue != null) return change.colorHue;
      return itemsById.get(itemId)?.colorHue ?? null;
    },
    [colorChanges, itemsById],
  );

  const handleToggleFavorite = React.useCallback(
    (itemId: string) => {
      const current = itemFavoritesByGroup[selectedGroupId] ?? [];
      const next = favoriteSet.has(itemId)
        ? current.filter((id) => id !== itemId)
        : [...current, itemId];
      setItemFavoritesByGroup({
        ...itemFavoritesByGroup,
        [selectedGroupId]: next,
      });
    },
    [
      itemFavoritesByGroup,
      selectedGroupId,
      favoriteSet,
      setItemFavoritesByGroup,
    ],
  );

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) {
        if (!over && dragSnapshotRef.current) {
          setItemFavoritesByGroup({
            ...itemFavoritesByGroupRef.current,
            [selectedGroupId]: dragSnapshotRef.current.favoriteIds,
          });
          setItemOrderByGroup({
            ...itemOrderByGroupRef.current,
            [selectedGroupId]: dragSnapshotRef.current.orderedIds,
          });
        }
        dragSnapshotRef.current = null;
        lastDragTargetRef.current = null;
        return;
      }
      dragSnapshotRef.current = null;
      lastDragTargetRef.current = null;
    },
    [selectedGroupId, setItemFavoritesByGroup, setItemOrderByGroup],
  );

  const handleDragStart = React.useCallback((_event: DragStartEvent) => {
    dragSnapshotRef.current = {
      favoriteIds: favoriteIdsRef.current,
      orderedIds: orderedIdsRef.current,
    };
    lastDragTargetRef.current = null;
  }, []);

  const handleDragOver = React.useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const overId = String(over.id);
      if (lastDragTargetRef.current === `${active.id}:${overId}`) return;
      lastDragTargetRef.current = `${active.id}:${overId}`;

      const nextFavoriteIds = updateFavoriteOrdering(
        favoriteIdsRef.current,
        String(active.id),
        overId,
      );
      const nextOrderedIds = updateOrderedIds(
        orderedIdsRef.current,
        String(active.id),
        overId,
      );

      favoriteIdsRef.current = nextFavoriteIds;
      orderedIdsRef.current = nextOrderedIds;
      setItemFavoritesByGroup({
        ...itemFavoritesByGroupRef.current,
        [selectedGroupId]: nextFavoriteIds,
      });
      setItemOrderByGroup({
        ...itemOrderByGroupRef.current,
        [selectedGroupId]: nextOrderedIds,
      });
    },
    [selectedGroupId, setItemFavoritesByGroup, setItemOrderByGroup],
  );

  const handleDragCancel = React.useCallback(
    (_event: DragCancelEvent) => {
      if (dragSnapshotRef.current) {
        setItemFavoritesByGroup({
          ...itemFavoritesByGroupRef.current,
          [selectedGroupId]: dragSnapshotRef.current.favoriteIds,
        });
        setItemOrderByGroup({
          ...itemOrderByGroupRef.current,
          [selectedGroupId]: dragSnapshotRef.current.orderedIds,
        });
      }
      dragSnapshotRef.current = null;
      lastDragTargetRef.current = null;
    },
    [selectedGroupId, setItemFavoritesByGroup, setItemOrderByGroup],
  );

  const handleItemColorChange = React.useCallback(
    (itemId: string, hue: number | null) => {
      setColorChanges((prev: Record<string, TouchModalColorChange>) => ({
        ...prev,
        [itemId]: { type: 'item', colorHue: hue },
      }));
    },
    [setColorChanges],
  );

  const handleGroupColorChange = React.useCallback(
    (hue: number | null) => {
      setColorChanges((prev: Record<string, TouchModalColorChange>) => ({
        ...prev,
        [selectedGroupId]: { type: 'group', colorHue: hue },
      }));
    },
    [selectedGroupId, setColorChanges],
  );

  const handleColorSwatchPress = React.useCallback((itemId: string) => {
    setColorPickerItemId((prev) => (prev === itemId ? null : itemId));
    setGroupColorPickerOpen(false);
  }, []);

  const colorPickerItem = colorPickerItemId
    ? itemsById.get(colorPickerItemId)
    : null;

  return (
    <div className="@EditModeGroupView flex flex-col gap-6">
      {/* GROUP HEADER WITH COLOR */}
      <div className="flex items-center gap-3">
        <div
          className="h-[44px] w-[35px] max-w-[35px] rounded-md border-2 border-neutral/20"
          style={groupStyle}
        />
        <div className="flex flex-1 flex-col gap-1">
          <div className="text-sm text-neutral/60">
            {t.inventoryItemsModal.materialType}
          </div>
          <div className="text-xl font-medium">{selectedGroupName}</div>
        </div>
        {canEditGroupColors && (
          <Button
            variant="ghost"
            size="icon"
            icon={<span className="h-5 w-5 rounded-full" />}
            className={cn(
              '!h-7 !min-h-0 !w-7 !min-w-0 !rounded-full !border-2 !p-0',
              groupColorPickerOpen
                ? '!border-foreground ring-2 ring-foreground/20'
                : '!border-foreground/20',
            )}
            style={{
              backgroundColor:
                hueToColor(effectiveGroupColorHue) ??
                'var(--color-foreground-10)',
            }}
            onPress={() => {
              setGroupColorPickerOpen((prev) => !prev);
              setColorPickerItemId(null);
            }}
          />
        )}
      </div>

      {/* GROUP COLOR PICKER */}
      {canEditGroupColors && groupColorPickerOpen && (
        <div className="rounded-lg border border-foreground/10 p-3">
          <div className="mb-2 text-sm font-medium">{selectedGroupName}</div>
          <HueColorPicker
            value={effectiveGroupColorHue ?? null}
            onChange={handleGroupColorChange}
          />
        </div>
      )}

      {(filteredFavoriteIds.length > 0 || filteredNonFavorites.length > 0) && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          {/* ★ FAVORITES */}
          {filteredFavoriteIds.length > 0 && (
            <div className="flex flex-col gap-4">
              <span className="text-xs font-medium text-foreground/50 uppercase">
                {t.scaleTicketPro.settings.favorites}
              </span>
              <SortableContext
                items={filteredFavoriteIds}
                strategy={horizontalListSortingStrategy}
              >
                <div className="grid grid-cols-3 gap-2">
                  {filteredFavoriteIds.map((id) => {
                    const item = itemsById.get(id);
                    if (!item) return null;
                    return (
                      <SortableEditItemCard
                        key={id}
                        id={id}
                        item={item}
                        effectiveColorHue={getItemColorHue(id)}
                        isFavorite={true}
                        canEditColors={canEditColors}
                        onToggleFavorite={() => handleToggleFavorite(id)}
                        onColorSwatchPress={() => handleColorSwatchPress(id)}
                      />
                    );
                  })}
                </div>
              </SortableContext>
            </div>
          )}

          {filteredFavoriteIds.length > 0 &&
            filteredNonFavorites.length > 0 && (
              <hr className="m-0 border-t-2 border-neutral/15" />
            )}

          {/* ALL ITEMS */}
          {filteredNonFavorites.length > 0 && (
            <div className="flex flex-col gap-4">
              <span className="text-xs font-medium text-foreground/50 uppercase">
                {t.scaleTicketPro.settings.allItems}
              </span>
              <SortableContext
                items={filteredNonFavorites.map((item) => item.id)}
                strategy={horizontalListSortingStrategy}
              >
                <div className="grid grid-cols-3 gap-2">
                  {filteredNonFavorites.map((item) => (
                    <SortableEditItemCard
                      key={item.id}
                      id={item.id}
                      item={item}
                      effectiveColorHue={getItemColorHue(item.id)}
                      isFavorite={false}
                      canEditColors={canEditColors}
                      onToggleFavorite={() => handleToggleFavorite(item.id)}
                      onColorSwatchPress={() => handleColorSwatchPress(item.id)}
                    />
                  ))}
                </div>
              </SortableContext>
            </div>
          )}
        </DndContext>
      )}

      {/* INLINE ITEM COLOR PICKER */}
      {canEditColors && colorPickerItem && (
        <div className="rounded-lg border border-foreground/10 p-3">
          <div className="mb-2 text-sm font-medium">{colorPickerItem.name}</div>
          <HueColorPicker
            value={
              colorChanges[colorPickerItem.id]?.colorHue ??
              colorPickerItem.colorHue ??
              null
            }
            onChange={(hue) => handleItemColorChange(colorPickerItem.id, hue)}
          />
        </div>
      )}
    </div>
  );
};
